import React from 'react';
import { getStatusColor, getStatusLabel } from '../utils/helpers';

export const StatusBadge = ({ status, className = '' }) => {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${getStatusColor(
        status
      )} ${className}`}
      data-testid={`status-badge-${status}`}
    >
      {getStatusLabel(status)}
    </span>
  );
};

export const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'slate' }) => {
  const colorClasses = {
    slate: 'text-slate-900',
    green: 'text-emerald-600',
    blue: 'text-blue-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  };

  const iconBg = {
    slate: 'bg-slate-100',
    green: 'bg-emerald-50',
    blue: 'bg-blue-50',
    amber: 'bg-amber-50',
    red: 'bg-red-50',
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col gap-2 hover:border-slate-300 transition-colors relative overflow-hidden group" data-testid="metric-card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="section-label mb-2">{title}</p>
          <p className={`font-mono text-2xl font-medium tracking-tight ${colorClasses[color]}`} data-testid="metric-value">{value}</p>
          {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {Icon && (
          <div className={`w-10 h-10 ${iconBg[color]} rounded-sm flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${colorClasses[color]}`} strokeWidth={1.5} />
          </div>
        )}
      </div>
      {trend && (
        <div className="mt-2 flex items-center text-xs">
          <span className={`font-mono font-medium ${trend.isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend.isPositive ? '+' : ''}{trend.value}
          </span>
          <span className="text-slate-400 ml-2">{trend.label}</span>
        </div>
      )}
    </div>
  );
};

export const PageHeader = ({ title, description, action }) => {
  return (
    <div className="mb-8" data-testid="page-header">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
        </div>
        {action && <div>{action}</div>}
      </div>
    </div>
  );
};

export const LoadingSpinner = ({ size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };

  return (
    <div className="flex items-center justify-center p-12">
      <div
        className={`${sizeClasses[size]} border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin`}
      ></div>
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, description, action }) => {
  return (
    <div className="text-center py-12" data-testid="empty-state">
      {Icon && (
        <div className="w-14 h-14 bg-slate-100 rounded-sm flex items-center justify-center mx-auto mb-4">
          <Icon className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
        </div>
      )}
      <h3 className="font-heading text-base font-semibold text-slate-700 mb-1">{title}</h3>
      {description && <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">{description}</p>}
      {action && <div>{action}</div>}
    </div>
  );
};
